import { auth } from '../constants/firebaseModular';

const CLOUDFUNCTIONS_URI = import.meta.env.VITE_CLOUDFUNCTIONS_URI;

interface ApplyEmployeeWitnessesResponse {
  success: boolean;
  data?: {
    witness1Name: string;
    witness2Name: string;
    appliedAt: number;
  };
  error?: string;
}

interface RemoveEmployeeWitnessesResponse {
  success: boolean;
  error?: string;
}

/**
 * Apply MIDEO employee witness signatures to a patient's chart.
 * Calls the employeeWitnessesApi/apply Cloud Function.
 *
 * @param patientId - The patient's UID
 * @returns Promise with success status and witness data
 */
export const applyEmployeeWitnesses = async (
  patientId: string
): Promise<ApplyEmployeeWitnessesResponse> => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('User is not authenticated');
  }

  const response = await fetch(`${CLOUDFUNCTIONS_URI}/employeeWitnessesApi/apply`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ patientId }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to apply employee witnesses');
  }

  return data;
};

/**
 * Remove MIDEO employee witness signatures from a patient's chart.
 * Resets witness fields to allow patient to use DocuSign flow instead.
 * Calls the employeeWitnessesApi/remove Cloud Function.
 *
 * @param patientId - The patient's UID
 * @returns Promise with success status
 */
export const removeEmployeeWitnesses = async (
  patientId: string
): Promise<RemoveEmployeeWitnessesResponse> => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('User is not authenticated');
  }

  const response = await fetch(`${CLOUDFUNCTIONS_URI}/employeeWitnessesApi/remove`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ patientId }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to remove employee witnesses');
  }

  return data;
};
